// @flow

import qs from 'querystring';
import * as React from 'react';
import {useCallback, useContext, useMemo, useReducer} from 'react';
import {contextStubFunction} from 'docs/util';
import {useAccount} from 'docs/data';
import type {Article} from 'docs/data';
import {ArticleReducer} from './ArticleReducer';
import {loadArticle} from './actions/loadArticle';
import type {ArticleCacheKeys} from './ArticleCacheKeys';
import type {ArticleReducerState} from './ArticleReducer';

const PREFETCH_PATHS = [
  '/docs/payments',
  '/docs/business',
  '/docs/financial-services',
  '/docs/security',
  '/docs/development',
  '/docs/products',
];

export type GetArticleContentOptions = {
  isPrefetch?: boolean,
};

export type GetArticleContentFunction = (
  path: string,
  cacheKeys: ArticleCacheKeys,
  options?: GetArticleContentOptions,
) => Promise<any>;

export type ArticleActions = {|
  getArticleContent: GetArticleContentFunction,
  loadArticle: (url: string, cacheKeys: ArticleCacheKeys) => Promise<any>,
  prefetchArticles: () => void,
|};

export type ArticleContextType = {
  ...ArticleReducerState,
  ...ArticleActions,
};

const ArticleContext = React.createContext<ArticleContextType>({
  article: ({}: any),
  isLoading: false,
  getArticleContent: contextStubFunction('Article'),
  loadArticle: contextStubFunction('Article'),
  prefetchArticles: contextStubFunction('Article'),
});

type ArticleProviderProps = {|
  article: Article,
  baseUrl: string,
  children?: React.Node,
|};

export const ArticleProvider = (props: ArticleProviderProps) => {
  const {account} = useAccount();

  const [state, dispatch] = useReducer(ArticleReducer, {
    article: props.article,
    isLoading: false,
  });

  const getArticleContent = useMemo(
    () => async (
      path: string,
      cacheKeys: ArticleCacheKeys,
      options: GetArticleContentOptions = {},
    ) => {
      const url = new URL(path, props.baseUrl);
      const query =
        url.search?.length > 0 ? qs.parse(url.search.substr(1)) : {};
      url.search = qs.stringify({
        ...query,
        ...cacheKeys,
      });

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Stripe-Docs-Prefetched': options.isPrefetch ? 'true' : 'false',
        },
      });

      if (!response.body || !response.ok) {
        throw new Error(`Error loading article content for ${url.toString()}`);
      }

      return response.json();
    },
    [props.baseUrl],
  );

  // We prefetch top-level category pages to cache in the browser for faster tab switching.
  const prefetchArticles = useCallback(() => {
    if (!props.baseUrl?.match('dev.stripe.me')) {
      const cacheKeys = {
        locale: account.locale.code,
        country: account.country.code,
        merchant: account.merchant?.token,
        version: props.article?.version,
      };
      PREFETCH_PATHS.forEach((path) => {
        getArticleContent(path, cacheKeys, {isPrefetch: true});
      });
    }
  }, [
    getArticleContent,
    account.locale,
    account.country,
    account.merchant,
    props.baseUrl,
    props.article,
  ]);

  const context = {
    ...state,
    getArticleContent,
    loadArticle: loadArticle(getArticleContent, dispatch),
    prefetchArticles,
  };

  return (
    <ArticleContext.Provider value={context}>
      {props.children}
    </ArticleContext.Provider>
  );
};

export const useArticle = () => {
  return useContext(ArticleContext);
};
